import { useEffect } from "react";
import PlaylistPanel from "../../components/app/PlaylistPanel";
import GestureDetector from "../../components/GestureDetector";
import { ArticlePageSkeleton } from "../../components/skeleton/ArticlePageSkeleton";
import { displayToast } from "../../components/app/AppToast";
import { handleErrorContentNotFound } from "../../domains/app/utils/errorHandler";
import { useTranslation } from "react-i18next";
import { ArticlePageBody } from "../../features/article/components/ArticlePageBody";
import { ArticlePageHeader } from "../../features/article/components/ArticlePageHeader";
import { ArticlePageHero } from "../../features/article/components/ArticlePageHero";
// import { ArticlePageRelatedArticles } from "../../features/article/components/ArticlePageRelatedArticles";
import { ArticleSectionAuthors } from "../../features/article/components/sections/ArticleSectionAuthors";
import { ArticleSectionBackground } from "../../features/article/components/sections/ArticleSectionBackground";
import { ArticleSectionConclusion } from "../../features/article/components/sections/ArticleSectionConclusion";
import { ArticleSectionDisclaimer } from "../../features/article/components/sections/ArticleSectionDisclaimer";
import { ArticleSectionDisclosure } from "../../features/article/components/sections/ArticleSectionDisclosure";
import { ArticleSectionDoi } from "../../features/article/components/sections/ArticleSectionDoi";
import { ArticleSectionEditorial } from "../../features/article/components/sections/ArticleSectionEditorial";
import { ArticleSectionWorksScore } from "../../features/article/components/sections/ArticleSectionWorksScore";
import { ArticleSectionFigures } from "../../features/article/components/sections/ArticleSectionFigures";
import { ArticleSectionKeywords } from "../../features/article/components/sections/ArticleSectionKeywords";
import { ArticleSectionLimitations } from "../../features/article/components/sections/ArticleSectionLimitations";
import { ArticleSectionMethodology } from "../../features/article/components/sections/ArticleSectionMethodology";
import { ArticleSectionObjectives } from "../../features/article/components/sections/ArticleSectionObjectives";
import { ArticleSectionResults } from "../../features/article/components/sections/ArticleSectionResults";
import { ArticleSectionOffLabel } from "../../features/article/components/sections/ArticleSectionOffLabel";
import { ArticleSectionTldr } from "../../features/article/components/sections/ArticleSectionTldr";
import { ArticleSectionAbstract } from "../../features/article/components/sections/ArticleSectionAbstract";
import DiscoverableRoomBlock from "../../components/room/DiscoverableRoomBlock";
import ArticlePageTabs from "../../features/article/components/ArticlePageTabs";
import { ArticlePageChatBar } from "../../features/article/components/ArticlePageChatBar";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { FeedbackCard } from "../../components/ui/FeedbackCard";
import { useArticleData } from "../../features/article/hooks/useArticleData";
import { useApiQuery } from "../../services/api";
import {
  useArticlePageUIState,
  type ArticlePageTabName,
} from "../../features/article/hooks/useArticlePageUIState";
import { useArticlePageScroll } from "../../features/article/hooks/useArticlePageScroll";
import { useArticleAnalytics } from "../../features/article/hooks/useArticleAnalytics";
import { useArticleAudio } from "../../features/article/hooks/useArticleAudio";
import { useArticleAudioPlayback } from "../../features/article/hooks/useArticleAudioPlayback";
import { useArticleInteractions } from "../../features/article/hooks/useArticleInteractions";
import { useArticleSwipe } from "../../features/article/hooks/useArticleSwipe";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { DesktopArticlePage } from "./desktop/DesktopArticlePage";

export default function ArticlePage(props: {
  slug: string;
  roomId: string | null;
}) {
  if (isDesktopLayout()) {
    return <DesktopArticlePage slug={props.slug} roomId={props.roomId} />;
  }
  return <MobileArticlePage {...props} />;
}

function MobileArticlePage(props: { slug: string; roomId: string | null }) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const {
    article,
    articleError,
    discoverableRoom,
    isDiscoverableRoomJoinable,
    articleImages,
  } = useArticleData(props);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    props.roomId !== null ? { param: { id: props.roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const uiState = useArticlePageUIState({
    slug: props.slug,
    article,
    // Null covers both "no room" and "the room row is out of reach", which is
    // the standing case for a shared link opened without a session: this query
    // runs against `/webapp/private/rooms/:id`, which needs a session.
    roomIsPublic: currentRoom === null ? null : currentRoom.isPublic,
  });
  const { headerRef, heroBottomMarkerRef, isHeroOnScreen } =
    useArticlePageScroll();
  const { handleViewArticleSource, markConsumed, noteExit, noteTab } =
    useArticleAnalytics(
      article,
      uiState.isArticleReadMarkerOnScreen,
      currentRoom,
      uiState.contentTopRef
    );
  const { isSynthesizing, synthesize } = useArticleAudio(article, props.slug);
  const { isSaved, toggleSaveToPlaylists, shareArticle } =
    useArticleInteractions(article);
  const { swipeList } = useArticleSwipe(props.slug);
  const { isTrackPlaying, isThisArticlePlaying, toggleAudio } =
    useArticleAudioPlayback({
      article,
      swipeList,
      synthesize,
    });

  useEffect(() => {
    if (articleError !== null && !handleErrorContentNotFound(articleError)) {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }, [articleError, t]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [props.slug]);

  if (articleError !== null) return null;
  if (!article) return <ArticlePageSkeleton />;

  const canShare = currentUser !== null;
  const handleShareArticle = () => {
    if (currentUser === null) return;
    noteExit("share");
    void shareArticle(article, currentUser);
  };
  // contentTopRef is deliberately NOT in here. The tabs render twice — once in
  // the sticky header and once in the body — and both stay mounted, because the
  // header copy is hidden with transform and opacity rather than unmounted. One
  // ref object shared by two mounted elements resolves to whichever React
  // attaches last, so the scroll anchor was decided by render order. It has to
  // be the body copy: the header sits OUTSIDE the scrolling element, so anchoring
  // there makes useArticleAnalytics walk up from a node that never scrolls and
  // fall back to `window`, which never scrolls either, and no scroll would mark
  // an article read.
  const tabProps = {
    enabledTabs: uiState.enabledTabs,
    activeTab: uiState.activeTab,
    setActiveTab: uiState.setActiveTab,
    onTabChange: (tab: ArticlePageTabName) => {
      markConsumed();
      noteTab(tab);
    },
  };

  return (
    <ScrollableLayout
      style={{
        overscrollBehavior: "none",
      }}
      header={
        <ArticlePageHeader
          roomId={props.roomId}
          hasRoom={currentRoom !== null}
          articleTitle={article.title}
          isSaved={isSaved}
          onOpenPlaylists={() => {
            uiState.setPlaylistsPanelVisible(true);
          }}
          onShare={handleShareArticle}
          canShare={canShare}
          headerRef={headerRef}
          isHeroOnScreen={isHeroOnScreen}
          tabsProps={{ ...tabProps, contentTopRef: null }}
          onBack={() => {
            noteExit("back");
          }}
        />
      }
    >
      <GestureDetector>
        <ArticlePageHero
          article={article}
          onToggleAudio={() => {
            void toggleAudio();
          }}
          isPlaying={isThisArticlePlaying}
          isSynthesizing={isSynthesizing}
        />
        <DiscoverableRoomBlock
          offer={discoverableRoom}
          isRoomJoinable={isDiscoverableRoomJoinable}
        />
        <ArticlePageTabs {...tabProps} contentTopRef={uiState.contentTopRef} />
        <div ref={heroBottomMarkerRef} />

        {/* Outside the tab condition: the warning is compliance copy, so it
            shows on every tab and for an article that has no overview. */}
        <div className="px-8 pt-6 empty:hidden">
          <ArticleSectionOffLabel article={article} />
        </div>

        {uiState.activeTab === "overview" && uiState.isAllowedArticle && (
          <ArticlePageBody>
            <div className="flex flex-col gap-4">
              <div className="mb-2">
                <ArticleSectionWorksScore
                  layout="phone"
                  article={article}
                  sheetTarget={uiState.worksScoreSheetTarget}
                  onSheetTargetChange={uiState.setWorksScoreSheetTarget}
                />
              </div>

              <ArticleSectionTldr article={article} />

              <div ref={uiState.articleReadMarkerRef} />

              <ArticleSectionFigures articleImages={articleImages} />

              <ArticleSectionDisclaimer />
            </div>
          </ArticlePageBody>
        )}

        {uiState.activeTab === "abstract" && (
          <ArticlePageBody>
            {uiState.hasDigestContent &&
            uiState.isAllowedArticle &&
            uiState.canDisplayContent ? (
              <>
                <ArticleSectionBackground
                  article={article}
                  isExpanded={uiState.isBackgroundExpanded}
                  setExpand={uiState.setBackgroundExpanded}
                />
                <ArticleSectionObjectives
                  article={article}
                  isExpanded={uiState.isObjectivesExpanded}
                  setExpand={uiState.setObjectivesExpanded}
                />
                <ArticleSectionMethodology
                  article={article}
                  isExpanded={uiState.isMethodologyExpanded}
                  setExpand={uiState.setMethodologyExpanded}
                />
                <ArticleSectionResults
                  article={article}
                  isExpanded={uiState.isResultsExpanded}
                  setExpand={uiState.setResultsExpanded}
                />
                <ArticleSectionLimitations
                  article={article}
                  isExpanded={uiState.isLimitationsExpanded}
                  setExpand={uiState.setLimitationsExpanded}
                />
                <ArticleSectionConclusion
                  article={article}
                  isExpanded={uiState.isConclusionExpanded}
                  setExpand={uiState.setConclusionExpanded}
                />
              </>
            ) : uiState.hasShowableAbstract ? (
              <ArticleSectionAbstract article={article} />
            ) : null}
            <div ref={uiState.articleReadMarkerRef} />

            <ArticleSectionDisclaimer />
          </ArticlePageBody>
        )}

        {uiState.activeTab === "source" && (
          <ArticlePageBody>
            <ArticleSectionDoi
              article={article}
              handleViewArticleSource={handleViewArticleSource}
            />
            <section data-gtm-section="feedback-card" className="pb-10!">
              <FeedbackCard />
            </section>
            <ArticleSectionAuthors article={article} />
            <ArticleSectionDisclosure article={article} />
            <ArticleSectionKeywords article={article} />
            <ArticleSectionEditorial
              article={article}
              handleViewArticleSource={handleViewArticleSource}
            />
          </ArticlePageBody>
        )}

        {/* {uiState.activeTab === "similarArticles" && (
          <ArticlePageRelatedArticles
            relatedArticles={relatedArticles}
            isLoadingRelatedArticles={isLoadingRelatedArticles}
          />
        )} */}
        {/* Clears the fixed bottom composer (ArticlePageChatBar): its own
            height plus the home-indicator inset. 96px was tuned before the
            safe area existed, so on iOS the tail of the last section — the
            keywords — stayed under the bar. The bar spends 125px (10 pt + the
            95px composer + 20 pb), so 140 keeps the same slack it always had. */}
        <div className="h-[calc(140px+var(--safe-area-bottom))]" />
      </GestureDetector>

      <ArticlePageChatBar
        article={article}
        isTrackPlaying={isTrackPlaying}
        placement="fixed"
        onOpenChat={() => {
          noteExit("chat");
        }}
      />

      <PlaylistPanel
        isVisible={uiState.isPlaylistsPanelVisible}
        contentToAdd={article}
        onClose={() => {
          uiState.setPlaylistsPanelVisible(false);
        }}
        onSave={({ add, remove }: { add: string[]; remove: string[] }) =>
          toggleSaveToPlaylists(article, { add, remove })
        }
      />
    </ScrollableLayout>
  );
}
